import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LuArrowUpRight,
  LuCode,
  LuGlobe,
  LuHeartPulse,
  LuMonitorSmartphone,
  LuShoppingBag,
  LuSmartphone,
} from 'react-icons/lu';
import { getPortfolioDetailPath } from '../../utils/portfolioMatching';

const FALLBACK_IMAGE =
  'https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop';

function getCategoryIcon(category = '') {
  const key = category.toLowerCase();
  if (key.includes('e-commerce') || key.includes('commerce') || key.includes('shop')) {
    return LuShoppingBag;
  }
  if (key.includes('mobile') || key.includes('app')) {
    return LuSmartphone;
  }
  if (key.includes('health') || key.includes('portal')) {
    return LuHeartPulse;
  }
  if (key.includes('web')) {
    return LuGlobe;
  }
  if (key.includes('dashboard') || key.includes('fintech')) {
    return LuMonitorSmartphone;
  }
  return LuCode;
}

function resolveImageSrc(src) {
  if (!src || typeof src !== 'string') return FALLBACK_IMAGE;
  const trimmed = src.trim();
  if (!trimmed) return FALLBACK_IMAGE;
  // Encode spaces in local paths like "/img/pos system.png"
  if (trimmed.startsWith('/') || trimmed.startsWith('./')) {
    return trimmed.replace(/ /g, '%20');
  }
  return trimmed;
}

const PortfolioCard = ({ item, index, projects = [] }) => {
  const detailPath = getPortfolioDetailPath(item, projects);
  const Icon = getCategoryIcon(item.category || item.title);
  const [imageSrc, setImageSrc] = useState(() => resolveImageSrc(item.image));

  useEffect(() => {
    setImageSrc(resolveImageSrc(item.image));
  }, [item.image]);

  return (
    <motion.article
      initial={{ opacity: 0, y: 25 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="group relative flex h-full flex-col overflow-hidden rounded-2xl bg-white shadow-[0_14px_40px_rgba(15,23,42,0.12)] dark:bg-[#111111] dark:shadow-[0_14px_40px_rgba(0,0,0,0.45)]"
    >
      <div className="relative h-64 w-full overflow-hidden bg-[#eceff3] sm:h-72">
        <img
          src={imageSrc}
          alt={item.title}
          loading="lazy"
          onError={() => {
            if (imageSrc !== FALLBACK_IMAGE) setImageSrc(FALLBACK_IMAGE);
          }}
          className="h-full w-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
        />
      </div>

      <div className="relative flex flex-1 flex-col px-6 pb-6 pt-7">
        <div className="absolute -top-7 right-5 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-black shadow-[0_8px_20px_rgba(243,199,21,0.45)]">
          <Icon size={24} strokeWidth={2.25} />
        </div>

        <p className="mb-2 text-xs font-extrabold uppercase tracking-[0.18em] text-primary">
          {item.category || 'Project'}
        </p>

        <h3 className="mb-3 pr-12 text-xl font-extrabold leading-snug text-[#111827] dark:text-white md:text-[1.35rem]">
          {item.title}
        </h3>

        <p className="mb-6 flex-1 text-sm font-normal leading-relaxed text-[#6b7280] dark:text-gray-400 line-clamp-3">
          {item.description}
        </p>

        <div className="mt-auto flex justify-end">
          <Link
            to={detailPath}
            aria-label={`Read more about ${item.title}`}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border-2 border-primary text-primary transition-all duration-300 hover:bg-primary hover:text-black"
          >
            <LuArrowUpRight size={20} strokeWidth={2.25} />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default PortfolioCard;
